import type {
  AuditAccessControlResponse,
  AuditAccessRequestsResponse,
  AuditAiWorkflow,
  AuditDetail,
  AuditDigitizationRecord,
  AuditPatchPayload,
  AuditsParams,
  AuditsResponse,
  AuditTypesResponse,
  AuditVersionHistoryResponse,
  CreateAuditPayload,
  CreateDigitizedSyncHistoryPayload,
  ImportActionsPayload,
} from '@/api/audits/types';
import {
  API_ROOT_URL,
  AUDITS_SERVICE_URL,
  BACKEND_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchAudits(
  params: AuditsParams,
): Promise<AuditsResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page - 1));
  qs.set('count', String(params.count));
  if (params.companyId) qs.set('companyId', params.companyId);
  qs.set('withAllStatus', String(params.withAllStatus));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  if (params.isForNetwork !== undefined)
    qs.set('isForNetwork', String(params.isForNetwork));
  if (params.isCompanyProfile !== undefined)
    qs.set('isCompanyProfile', String(params.isCompanyProfile));
  return apiFetch<AuditsResponse>(
    `/v1/audits?${qs.toString()}`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function fetchAuditById(id: string): Promise<AuditDetail> {
  const res = await apiFetch<{ data: AuditDetail } | AuditDetail>(
    `/v1/audits/${id}`,
    {},
    AUDITS_SERVICE_URL,
  );
  return 'data' in res && (res as { data: AuditDetail }).data
    ? (res as { data: AuditDetail }).data
    : (res as AuditDetail);
}

export async function fetchAuditVersionHistory(
  id: string,
  qs: string,
): Promise<AuditVersionHistoryResponse> {
  return apiFetch<AuditVersionHistoryResponse>(
    `/v1/audits/${id}/versions?${qs}`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function fetchAuditTypes(): Promise<AuditTypesResponse> {
  const qs = new URLSearchParams({ sort: 'name:ASC', count: '200' });
  return apiFetch<AuditTypesResponse>(
    `/v1/audit-types?${qs.toString()}`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function patchAudit(
  id: string,
  body: AuditPatchPayload,
): Promise<void> {
  await apiFetch<void>(
    `/v1/audits/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    AUDITS_SERVICE_URL,
  );
}

export async function requestAuditAccess(id: string): Promise<void> {
  await apiFetch<void>(
    `/v1/audits/${id}/request`,
    { method: 'POST' },
    AUDITS_SERVICE_URL,
  );
}

export async function cancelAuditRequest(id: string): Promise<void> {
  await apiFetch<void>(`/v3/audits/${id}/cancel-request`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function bulkArchiveAudits(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v1/audits/bulk-archive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    AUDITS_SERVICE_URL,
  );
}

export async function bulkUnarchiveAudits(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v1/audits/bulk-unarchive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    AUDITS_SERVICE_URL,
  );
}

export async function createAudit(
  body: CreateAuditPayload,
): Promise<{ data: { id: string } }> {
  // Create was migrated out of the legacy audits-service to the new
  // `POST /api/assets`-style gateway route (`POST /api/audits`, `apiSuccess`
  // envelope) — mirrors the legacy create body. The old audits-service path no
  // longer exists.
  return apiFetch<{ data: { id: string } }>(
    '/api/audits',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function triggerAuditAiWorkflow(body: {
  auditFileId?: string;
  capFileId?: string;
}): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>(
    '/capa/process',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function getAuditAiWorkflow(
  workflowId: string,
): Promise<AuditAiWorkflow> {
  const res = await apiFetch<{ data: AuditAiWorkflow }>(
    `/capa/workflows/${workflowId}`,
    {},
    BACKEND_URL,
  );
  return res.data;
}

export async function cancelAuditAiWorkflow(workflowId: string): Promise<void> {
  await apiFetch<void>(
    '/capa/process/cancel',
    { method: 'DELETE', body: JSON.stringify({ workflowId }) },
    BACKEND_URL,
  );
}

export async function importCapaActions(
  body: ImportActionsPayload,
): Promise<{ actionIds: string[] }> {
  const res = await apiFetch<{ data: { actionIds: string[] } }>(
    '/capa/import-actions',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function getAuditAccessControl(
  auditId: string,
): Promise<{ data: AuditAccessControlResponse }> {
  // Refactored into modules/companies, now served under the `/api/assets`
  // gateway mount (`GET /assets/:assetId/access-control`, `apiSuccess` envelope).
  return apiFetch<{ data: AuditAccessControlResponse }>(
    `/api/assets/${auditId}/access-control`,
    {},
    API_ROOT_URL,
  );
}

export async function getAuditAccessRequests(
  auditId: string,
): Promise<AuditAccessRequestsResponse> {
  const filter = encodeURIComponent(JSON.stringify({ status: 'REQUESTED' }));
  return apiFetch<AuditAccessRequestsResponse>(
    `/v1/audit-access-requests/${auditId}?filter=${filter}`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function shareAuditWithCompany(params: {
  auditId: string;
  companyId: string;
}): Promise<void> {
  // `POST /assets/:assetId/shares` — one company per call, returns 204.
  await apiFetch<void>(
    `/api/assets/${params.auditId}/shares`,
    {
      method: 'POST',
      body: JSON.stringify({ companyId: params.companyId }),
    },
    API_ROOT_URL,
  );
}

export async function submitAiFeedback(body: {
  fileId: string;
  assetId: string;
  assetType: string;
  aiProcessType: string;
  vote: 0 | 1;
  /** Free-text feedback. Captured by the UI; only sent when the backend
   *  accepts it (omitted from the wire payload otherwise to avoid 500s). */
  text?: string;
}): Promise<void> {
  // BE currently 500s when an unknown `text` field is included. Strip it
  // from the wire payload until the route is extended; the UI still
  // collects the message so we can flip this back on without UX changes.
  const { text: _text, ...payload } = body;
  await apiFetch<void>(
    '/ai-feedback',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// ─── AI suggestions sub-flow ────────────────────────────────────────────────

export type CapaWorkflowType =
  | 'CLASSIFICATION'
  | 'FIELD_EXTRACTION'
  | 'CAPA_CLASSIFICATION'
  | 'CAPA_CAP_FILE_DATA_EXTRACTION'
  | 'EXTRACT_FINDINGS'
  | 'CAPS_CREATION';

export interface CapaWorkflow {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  percentage: number;
  metadata?: Record<string, unknown> | null;
  steps?: { type: CapaWorkflowType; status: string }[];
}

/** Append additional processing steps to an existing capa workflow and
 *  re-open it to PROCESSING. Used to layer EXTRACT_FINDINGS + CAPS_CREATION
 *  on top of an already-completed upload workflow (AI suggestions flow). */
export async function appendWorkflowSteps(
  workflowId: string,
  workflows: CapaWorkflowType[],
): Promise<CapaWorkflow> {
  const res = await apiFetch<{ data: CapaWorkflow }>(
    `/capa/workflows/${workflowId}/steps`,
    { method: 'POST', body: JSON.stringify({ workflows }) },
    BACKEND_URL,
  );
  return res.data;
}

/** Poll a capa workflow until terminal. Mirrors the upload-analysis path. */
export async function fetchCapaWorkflow(
  workflowId: string,
): Promise<CapaWorkflow> {
  const res = await apiFetch<{ data: CapaWorkflow }>(
    `/capa/workflows/${workflowId}`,
    {},
    BACKEND_URL,
  );
  return res.data;
}

// ─── Create-plan sub-flow ───────────────────────────────────────────────────

export interface CreateCapaPlanPayload {
  title: string;
  description?: string;
  companyId: string;
  /** Always 'AUDIT' from this flow (the audit IS the plan's source). */
  sourceType: 'AUDIT';
  type: 'AUDIT';
  sourceId: string;
  assignees?: string[];
  dueDate?: string | null;
  tags?: string[];
}

/** POST /capa/caps — creates a new corrective-action plan whose source is
 *  the audit just created. Returns the plan id used by the SelectActions
 *  step to link the chosen findings/actions. */
export async function createCapaPlan(
  payload: CreateCapaPlanPayload,
): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>(
    '/capa/caps',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
  return res.data;
}

/** PATCH /capa/actions/bulk-link-cap — attaches previously-created actions to
 *  a plan (cap). Used by the createPlan sub-flow's SelectActions step. */
export async function linkActionsToPlan(
  payload: { id: string; capId: string }[],
): Promise<void> {
  await apiFetch<void>(
    '/capa/actions/bulk-link-cap',
    { method: 'PATCH', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// ─── Digitized audits ───────────────────────────────────────────────────────

/** GET /audits/digitization/:auditId — the digitization record (progress +
 *  extracted content). Backend routes live on the monolith (`BACKEND_URL`,
 *  no `/v1`). Throws ApiError 404 (`digitization_not_found`) when the audit
 *  has not been digitized yet — the hook maps that to `null`. */
export async function fetchAuditDigitization(
  auditId: string,
): Promise<{ data: AuditDigitizationRecord }> {
  return apiFetch<{ data: AuditDigitizationRecord }>(
    `/audits/digitization/${auditId}`,
    {},
    BACKEND_URL,
  );
}

/** POST /audits/digitization — starts (or restarts) digitization for an audit.
 *  Throws ApiError 409 (`digitization_already_in_progress`) when a run is
 *  already active; the caller treats that as a benign "already analyzing". */
export async function startAuditDigitization(
  auditId: string,
): Promise<{ data: AuditDigitizationRecord }> {
  return apiFetch<{ data: AuditDigitizationRecord }>(
    '/audits/digitization',
    { method: 'POST', body: JSON.stringify({ auditId }) },
    BACKEND_URL,
  );
}

/** POST /audits/digitization/:auditId/history — records a datapoints-sync
 *  history entry after the user pushes verified answers to the profile. */
export async function createDigitizedAuditSyncHistory(
  auditId: string,
  body: CreateDigitizedSyncHistoryPayload,
): Promise<void> {
  await apiFetch<void>(
    `/audits/digitization/${auditId}/history`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

// ─── Sync digitized answers → company profile ───────────────────────────────
// Mirror of the v1 sync flow. The template-answer routes live on the main API
// gateway (`/api/companies/templates/...`), NOT the companies-service micro-
// service. `templateId` is an arbitrary profile/impact-area id (not the narrow
// TemplateId union), so these deliberately do not reuse `@/api/companies`.

/** Current company-profile answers for a template, keyed by retracedId. */
export async function fetchDigitizedProfileTemplate(
  templateId: string,
  companyId: string,
): Promise<{ data?: Record<string, unknown> }> {
  return apiFetch<{ data?: Record<string, unknown> }>(
    `/api/companies/templates/${templateId}/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
}

/** Write verified digitized answers into one template of the company profile. */
export async function patchDigitizedTemplateAnswer(
  templateId: string,
  body: {
    byCompanyId: string;
    forCompanyId: string;
    onBehalfOfCompanyId: string;
    answer: Record<string, unknown>;
  },
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/templates/${templateId}/answer`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
